import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Download, Delete, MoreHoriz } from '@mui/icons-material';
import { Button, Menu, MenuItem, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FileBlobUtils } from '@cosmotech/core';
import { FadingTooltip } from '@cosmotech/ui';
import { ACL_PERMISSIONS } from '../../../../../../services/config/accessControl';
import {
  useDatasetStorageRoot,
  useDeleteDatastoreFiles,
  useIsArchiveAvailable,
  useIsReportAvailable,
  useSelectedDataset,
  useSetValidationState,
  useUploadDatastoreFiles,
  useValidationState,
} from '../../../../../../state/hooks/DatastoreHooks';
import { useDataStoreFileDownload } from '../../../../DataStoreHooks';

const useStyles = makeStyles((theme) => ({
  tableOptionsMenuButton: {
    color: theme.palette.text.secondary,
  },
}));

const DownloadButton = ({ name, storagePath, handleClose }) => {
  const { t } = useTranslation();
  const downloadFile = useDataStoreFileDownload();
  const handleDownload = useCallback(() => {
    handleClose();
    downloadFile(storagePath)
      .then((data) => FileBlobUtils.downloadFileFromData(data.data, name))
      .catch((error) => console.error(error));
  }, [downloadFile, storagePath, name, handleClose]);

  return (
    <MenuItem onClick={handleDownload}>
      <Download />
      {t('commoncomponents.datastore.filesList.fileOptions.downloadAsCsv', 'Download as CSV')}
    </MenuItem>
  );
};

DownloadButton.propTypes = {
  name: PropTypes.string.isRequired,
  storagePath: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
};

const DeleteButton = ({ name, storagePath, datasetId, handleClose }) => {
  const { t } = useTranslation();
  const uploadFiles = useUploadDatastoreFiles();
  const deleteFiles = useDeleteDatastoreFiles();
  const storageRoot = useDatasetStorageRoot();
  const isArchiveAvailable = useIsArchiveAvailable();
  const isReportAvailable = useIsReportAvailable();
  const validation = useValidationState();
  const setValidationState = useSetValidationState();

  const handleDelete = useCallback(() => {
    handleClose();
    try {
      const filePathsToDelete = [storagePath];
      if (isArchiveAvailable) filePathsToDelete.push(`${storageRoot}datastore.zip`);
      if (isReportAvailable) filePathsToDelete.push(`${storageRoot}validation_report.txt`);
      deleteFiles(filePathsToDelete);

      const updatedValidation = { ...validation, status: validation.status === 'draft' ? 'draft' : 'modified' };
      const fileData = new File([new Blob([JSON.stringify(updatedValidation)])], '_validation.json');
      uploadFiles([{ data: fileData, path: storageRoot }]);
      setValidationState(updatedValidation);
    } catch (error) {
      console.error(error);
    }
  }, [
    storagePath,
    handleClose,
    deleteFiles,
    uploadFiles,
    storageRoot,
    validation,
    isArchiveAvailable,
    isReportAvailable,
    setValidationState,
  ]);

  return (
    <MenuItem onClick={handleDelete}>
      <Delete color="error" />
      <Typography color="error.main">
        {t('commoncomponents.datastore.filesList.fileOptions.delete', 'Delete')}{' '}
        {name.split('.').slice(0, -1).join('.')}
      </Typography>
    </MenuItem>
  );
};

DeleteButton.propTypes = {
  name: PropTypes.string.isRequired,
  storagePath: PropTypes.string.isRequired,
  datasetId: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export const FileOptions = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dataset = useSelectedDataset();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const userPermissionsOnDataset = dataset?.security?.currentUserPermissions ?? [];
  const canUserDeleteFiles = userPermissionsOnDataset.includes(ACL_PERMISSIONS.DATASET.WRITE);

  return (
    <div>
      <FadingTooltip title={t('commoncomponents.datastore.filesList.fileOptions.tooltip', 'Additional file options')}>
        <Button onClick={handleClick} className={classes.tableOptionsMenuButton}>
          <MoreHoriz />
        </Button>
      </FadingTooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <DownloadButton name={props.name} storagePath={props.storagePath} handleClose={handleClose} />
        {canUserDeleteFiles && (
          <DeleteButton
            name={props.name}
            storagePath={props.storagePath}
            datasetId={props.datasetId}
            handleClose={handleClose}
          />
        )}
      </Menu>
    </div>
  );
};

FileOptions.propTypes = {
  name: PropTypes.string.isRequired,
  storagePath: PropTypes.string.isRequired,
  datasetId: PropTypes.string.isRequired,
};
